import { BeneficiaryInsightWidgetDTO } from '@repo/shared/dtos/beneficiary'
import { Button } from '@repo/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/dropdownMenu'
import { useT } from '@repo/ui/hooks'
import { FileDownIcon, MoreHorizontal, Pencil, Trash2, Users2 } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { useBeneficiaryListAsSheet } from '@/components/beneficiary/list/sheet/hook'
import { downloadCSV } from '@/components/helpers'
import { buildSearchParam, SearchParamsKeyNames, SearchParamsViewNames } from '@/routes'
import { deleteInsightWidget, downloadInsightWidgetCSV } from '@/store/beneficiary-insights/thunks'
import { useStoreDispatch } from '@/store/hooks'

export function Actions({ widget }: { widget: BeneficiaryInsightWidgetDTO }) {
  const navigate = useNavigate()
  const { t } = useT({ keyPrefix: 'admin.insights.list' })
  const { setOpen: setOpenBeneficiaryListAsSheet } = useBeneficiaryListAsSheet()
  const dispatch = useStoreDispatch()

  const handleEdit = () => {
    navigate(
      buildSearchParam(SearchParamsKeyNames.View, SearchParamsViewNames.INSIGHTS_EDIT, {
        id: widget.id,
      }),
    )
  }

  const handleShowBeneficiaries = () => {
    setOpenBeneficiaryListAsSheet(true, { filterTemplate: { name: '', filters: widget.filters } })
  }

  const handleDownloadCSV = async () => {
    const csv = await dispatch(downloadInsightWidgetCSV({ widgetId: widget.id })).unwrap()
    if (csv) {
      downloadCSV('beneficiaries-insight.csv', csv)
    }
  }

  const handleDelete = () => {
    if (confirm(t('actions.deleteConfirm'))) {
      void dispatch(deleteInsightWidget({ widgetId: widget.id }))
    }
  }

  const hasFilters = useMemo(() => widget.filters.length > 0, [widget.filters])
  const hasDimensions = useMemo(() => widget.dimensions.length > 0, [widget.dimensions])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">{t('openMenu')}</span>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleEdit}>
          <Pencil className="size-4" />
          <span>{t('actions.editButton')}</span>
        </DropdownMenuItem>

        {(hasFilters || hasDimensions) && (
          <>
            <DropdownMenuSeparator />
            {hasFilters && (
              <DropdownMenuItem onClick={handleShowBeneficiaries}>
                <Users2 className="size-4" />
                <span>{t('actions.showBeneficiaries')}</span>
              </DropdownMenuItem>
            )}
            {hasDimensions && (
              <DropdownMenuItem onClick={void handleDownloadCSV}>
                <FileDownIcon className="size-4" />
                <span>{t('actions.downloadCSVButton')}</span>
              </DropdownMenuItem>
            )}
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleDelete}>
          <Trash2 className="size-4" />
          <span>{t('actions.deleteButton')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
